import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import MDBox from 'components/MDBox';
import { Grid2 as Grid, Skeleton } from '@mui/material';
import MDTypography from 'components/MDTypography';
import _ from 'lodash';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import * as React from 'react';
import { useEffect, useState } from 'react';
import CardContent from '@mui/material/CardContent';
import { toBoolean } from 'utils/stringUtils';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useNotification } from 'hook/useNotification';
import { useLazyFindBySpecificationQuery } from 'services/entityApi';
import { fErrors } from 'utils/formatError';
import MDLink from 'components/MDLink';
import { NavLink as RouterLink } from 'react-router-dom';

export default function Tasks() {
  const { formatMessage: msg } = useIntl();
  const { getValue: conf } = useConf(useGetConfigurationQuery);

  const [sortedTasks, setSortedTasks] = useState([]);

  const tasksConf = conf(`tasks`);

  useEffect(() => {
    if (tasksConf?.content) {
      setSortedTasks(
        _.chain(tasksConf.content)
          .filter((taskConf) => toBoolean(taskConf.enabled, true))
          .sortBy(['level'])
          .value()
      );
    }
  }, [tasksConf.content]);

  return (
    <MDBox pt={4} pb={3} flex={1}>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {sortedTasks.map((taskConf, index) => {
          const { titleId, icon } = taskConf;
          const title = msg({
            id: titleId,
            defaultMessage: titleId
          });

          // const items = [];

          return (
            <Grid key={index} item xs={12} sm={6}>
              <Card variant="outlined">
                <MDBox
                  mx={2}
                  mt={-3}
                  py={2}
                  px={2}
                  variant="gradient"
                  bgColor="primary"
                  borderRadius="lg"
                  coloredShadow="primary"
                  sx={{ display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}
                >
                  {icon && (
                    <Icon fontSize="medium" sx={{ color: 'white !important' }}>
                      {icon}
                    </Icon>
                  )}
                  <MDTypography variant="h4" color="white" verticalAlign="middle" sx={{ mx: 2 }}>
                    {title}
                  </MDTypography>
                </MDBox>
                <CardContent>
                  <TaskContent taskConf={taskConf} />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </MDBox>
  );
}

TaskContent.propTypes = {
  taskConf: PropTypes.shape({
    code: PropTypes.string.isRequired,
    descriptionId: PropTypes.string,
    query: PropTypes.string.isRequired,
    queries: PropTypes.arrayOf(
      PropTypes.shape({
        query: PropTypes.string.isRequired
      })
    )
  }).isRequired
};

function TaskContent({ taskConf }) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [findBySpecification] = useLazyFindBySpecificationQuery();

  useEffect(() => {
    setLoading(true);
    setTotalCount(0);

    const count = async () => {
      try {
        let queries = [];
        if (taskConf.queries) {
          queries = taskConf.queries;
        } else if (taskConf.query) {
          queries = [taskConf.query];
        }

        const promises = queries.map((queryDef) =>
          findBySpecification({
            viewName: 'tasks',
            filter: queryDef.query,
            projection: 'entityMain',
            size: 1
          }).unwrap()
        );
        const results = await Promise.all(promises);

        return results.reduce((sum, result) => sum + result.decode().page.totalElements, 0);
      } catch (error) {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: fErrors(msg, error)
        });
        return -1;
      } finally {
        setLoading(false);
      }
    };

    count().then((totalCount) => setTotalCount(totalCount));
  }, [addNotification, findBySpecification, msg, taskConf.queries, taskConf.query, taskConf.code]);

  const description = msg({
    id: taskConf.descriptionId,
    defaultMessage: taskConf.descriptionId
  });
  const linkLabel =
    msg(
      {
        id: `page.tasks.${taskConf.code}.content.link`,
        defaultMessage: null
      },
      { count: totalCount }
    ) ||
    msg(
      {
        id: `page.tasks.content.link`,
        defaultMessage: null
      },
      { count: totalCount }
    );

  let contentComponent;
  if (totalCount > 0) {
    contentComponent = (
      <MDLink component={RouterLink} to={`${taskConf.code}`}>
        {linkLabel}
      </MDLink>
    );
  } else {
    contentComponent = (
      <MDTypography variant="body2" sx={{ fontStyle: 'italic' }}>
        {msg({
          id: `common.noData`
        })}
      </MDTypography>
    );
  }

  if (loading) {
    return <Skeleton variant="rounded" sx={{ height: 30, mt: 2 }} />;
  }
  return (
    <>
      <MDTypography variant="body2">{description}</MDTypography>
      <MDTypography sx={{ pt: 1 }}>{contentComponent}</MDTypography>
    </>
  );
}
